import { Program } from '@playbook-technologies/common-enums';
import { useEffect, useState } from 'react';
import Faq from '../Faq';
import MediaEditor from '../MediaEditor';
import * as media from '../media.css';
import {
  inputHolder,
  invalid,
  label as fieldLabel,
  textarea as fieldTextarea,
} from '../legacyForms.css';
import { bigHeading, fieldNarrow, fieldWide } from './collectionForms.css';
import StatusBar from '../StatusBar';
import { CollectionType } from './CollectionType';
import PillSelect, { SquarePill } from './PillSelect';
import { COLLECTION_SETTINGS } from './collections.settings';
import TagEditor from '../TagEditor';
import { PublishingBehaviourSelect } from './ProgramForm';
import { CollectionProgram } from './collections.types';
import { Toggle } from '../common/inputs/Toggle';
import { ColorPicker } from '../common/inputs/ColorPicker';
import ProgramReviews from './ProgramReviews';
import { TextField, Typography } from '@mui/material';
import { PrimaryButton } from '@client/new/components/button';
import { trackEvent } from '@services/tracking';
import { Typography as DsTypography } from '@design-system/components/typography/typography';
import { clsx } from 'clsx';
import { ProgramSwapsToggle } from '@client/new/features/exercise-swaps';
import { RemoveWorkoutLimitModal } from '@client/new/features/program-form';

type CategoryEditProps = {
  collection: CollectionProgram;
  disabledCollectionType: boolean;
  validationTree: any;
  router: any;
  route: unknown;
  onChange: (p: CollectionProgram) => void;
  typeChanged: (t: number) => void;
  title?: string;
  params: {
    influencerId: string;
    programId: string;
  };
  save: () => void;
  creationMode?: boolean;
  message?: string;
};

export const CategoryForm = (props: CategoryEditProps) => {
  // No setState is ever performed on these in the original component,
  // so they are effectively constant.
  const isModified = false;
  const isSaving = false;

  const routerWillLeave = () => {
    if (isModified && !isSaving) {
      return 'There are pending changes to the Collection, are you sure you want to leave without saving?';
    }
  };

  useEffect(() => {
    props.router.setRouteLeaveHook(props.route, routerWillLeave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hash = window.document.location.hash;
    if (hash) {
      const section = window.document.querySelector(hash);
      if (section) {
        window.setTimeout(() => {
          window.scrollTo({
            top: section.getBoundingClientRect().top,
            behavior: 'smooth',
          });
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isRemoveLimitOpen, setIsRemoveLimitOpen] = useState(false);

  const merge = (newData: Partial<CollectionProgram>) => {
    const data = { ...props.collection, ...newData };
    props.onChange(data);
  };

  const publishingBehaviourChanged = (value) => {
    const dropsLimit =
      value === Program.PublishingBehaviour.Bottom.value &&
      Boolean(props.collection.workoutLimit);
    if (dropsLimit) {
      setIsRemoveLimitOpen(true);
      return;
    }
    merge({ publishingBehaviour: value });
  };

  const confirmRemoveLimit = () => {
    merge({
      publishingBehaviour: Program.PublishingBehaviour.Bottom.value,
      workoutLimit: undefined,
    });
    setIsRemoveLimitOpen(false);
  };

  const typeChanged = (type) => {
    merge({ type });
    props.typeChanged(type);

    trackEvent({
      type: 'Category Added',
      payload: {
        'Category Type': type,
        'Tag Location': 'Category',
      },
    });
  };

  const toggleSection = (name: 'faq' | 'testimonials') => {
    const currentValues = props.collection.hiddenSections || {};
    merge({
      hiddenSections: {
        ...props.collection.hiddenSections,
        [name]: !currentValues[name],
      },
    });

    if (name === 'faq') {
      trackEvent({
        type: 'FAQ Toggled',
        payload: {
          'Collection Type': 'Category',
        },
      });
    }

    if (name === 'testimonials') {
      trackEvent({
        type: 'Reviews Toggled',
        payload: {
          'Collection Type': 'Category',
        },
      });
    }
  };

  const validate = (attribute: keyof CollectionProgram) => {
    // console.log(`[ProgramEdit] Validating '${attribute}'.`, data);

    if (attribute) {
      if (props.validationTree) {
        // console.log(`[ProgramEdit] Validating attribute '${attribute}'...`);
        if (
          props.validationTree &&
          props.validationTree[attribute] &&
          props.validationTree[attribute].length > 0
        ) {
          console.log(`[ProgramEdit] Attribute '${attribute}' is invalid.`);
          return invalid;
        }
        return null;
      } else {
        return null;
      }
    }
  };

  const mediaIntroChanged = (mediaIntro) => {
    merge({ mediaIntro });

    trackEvent({
      type: 'Intro Video Uploaded',
      payload: {
        'Collection Type': 'Category',
      },
    });
  };

  const mediaCoverChanged = (mediaCover) => {
    merge({ mediaCover });

    trackEvent({
      type: 'Cover Image Uploaded',
      payload: {
        'Collection Type': 'Category',
      },
    });
  };

  const targetAreaChanged = (targetArea) => {
    merge({ targetArea });

    trackEvent({
      type: 'Target Area Selected',
      payload: {
        'Tag Location': 'Category',
        'Target Area Type': targetArea,
      },
    });
  };

  const difficultyChanged = (values) => {
    merge({ difficulty: values[0] });

    trackEvent({
      type: 'Difficulty Selected',
      payload: {
        'Difficulty Type': values[0],
        'Tag Location': 'Category',
      },
    });
  };

  const equipmentChanged = (requiredEquipment) => {
    merge({ requiredEquipment });

    trackEvent({
      type: 'Equipment Added',
      payload: {
        'Equipment Type': requiredEquipment,
        'Tag Location': 'Category',
      },
    });
  };

  const nameChanged = (ev) => {
    merge({ name: ev.target.value });
  };

  const descriptionChanged = (ev) => {
    merge({ description: ev.target.value });
  };

  const workoutLimitChanged = (ev) => {
    const workoutLimit = ev.target.value
      ? parseInt(ev.target.value)
      : undefined;
    const update: Partial<CollectionProgram> = { workoutLimit };
    if (
      workoutLimit &&
      props.collection.publishingBehaviour !==
        Program.PublishingBehaviour.Top.value
    ) {
      update.publishingBehaviour = Program.PublishingBehaviour.Top.value;
    }
    merge(update);

    trackEvent({
      type: 'Limit Toggled',
      payload: {
        'Limit Number': workoutLimit || 0,
      },
    });
  };

  const tagsChanged = (tags) => {
    merge({ tags });
  };

  const faqChanged = (faq) => {
    merge({ faq });

    trackEvent({
      type: 'FAQ Added',
      payload: {
        'Collection Type': 'Category',
      },
    });
  };

  const locationChanged = (values) => {
    merge({ location: values[0] });

    trackEvent({
      type: 'Location Selected',
      payload: {
        'Location Type': values[0],
        'Tag Location': 'Category',
      },
    });
  };

  const colorChanged = (hex) => {
    merge({ color: hex.slice(1) });

    trackEvent({
      type: 'Color Selected',
      payload: {
        'Collection Type': 'Category',
      },
    });
  };

  if (!props.collection) {
    return <div className="programEdit">{props.message}</div>;
  }

  const save = props.save;

  return (
    <div className="categoryEdit">
      <main id="main">
        <div key="video" className="relative flex flex-col gap-8">
          <div
            className={
              'button-box absolute right-0 top-0 z-10' +
              (isSaving ? ' pointer-events-none' : '')
            }
          >
            <PrimaryButton
              isLoading={isSaving}
              onClick={() => save()}
              data-testid="subscription-category-save"
            >
              {props.creationMode ? 'Create' : 'Save'}
            </PrimaryButton>
          </div>

          <section>
            <div className="flex justify-between items-center pb-5">
              <div>
                {props.disabledCollectionType ? null : (
                  <CollectionType
                    type={props.collection.type}
                    disabled={props.disabledCollectionType}
                    onChange={typeChanged}
                  />
                )}
              </div>
            </div>
            <div className="pb-5">
              <ProgramSwapsToggle
                influencerId={props.collection.influencerId}
                programId={props.creationMode ? undefined : props.collection.id}
                value={props.collection.swapsEnabled}
                onChange={(swapsEnabled) => merge({ swapsEnabled })}
              />
            </div>
            <div className="flex flex-col md:flex-row gap-3">
              <div>
                <div className="heading-block">
                  <Typography>Intro video (optional)</Typography>
                </div>
                <div
                  className={clsx(media.mediaBoxSquare, validate('mediaIntro'))}
                >
                  <MediaEditor
                    variant="vertical"
                    metadata={props.collection?.mediaIntro}
                    onChange={mediaIntroChanged}
                    onRemove={mediaIntroChanged}
                    format="video"
                    accept="video/*"
                  />
                </div>
              </div>
              <div>
                <div className="heading-block">
                  <Typography>
                    Cover image <strong style={{ color: 'red' }}>*</strong>
                  </Typography>
                </div>
                <div
                  className={clsx(media.mediaBoxSquare, validate('mediaCover'))}
                >
                  <MediaEditor
                    variant="square"
                    metadata={props.collection.mediaCover}
                    onChange={mediaCoverChanged}
                    onRemove={mediaCoverChanged}
                    format="image"
                    accept="image/*"
                  />
                </div>
              </div>
            </div>

            <div className="mt-8">
              <div className={fieldWide}>
                <label className={fieldLabel} style={{ margin: 0 }}>
                  Title <strong style={{ color: 'red' }}>*</strong>
                </label>
                <DsTypography
                  variant="body"
                  size="medium"
                  color="N-80"
                  fontWeight="normal"
                  className="mb-2"
                >
                  This title will be shown to your users
                </DsTypography>
                <TextField
                  type="text"
                  onChange={nameChanged}
                  className={clsx(validate('name'))}
                  value={props.collection.name}
                  inputProps={{ maxLength: 50 }}
                  data-testid="subscription-category-name-input"
                />
              </div>
              <div className={fieldNarrow}>
                <label className={fieldLabel}>
                  Color <strong style={{ color: 'red' }}>*</strong>
                </label>
                <ColorPicker
                  value={'#' + props.collection.color}
                  onChange={colorChanged}
                  showColorBox
                />
              </div>
              <div className={fieldWide}>
                <label className={fieldLabel}>Description (optional)</label>
                <TextField
                  multiline
                  onChange={descriptionChanged}
                  value={props.collection.description}
                  inputProps={{ maxLength: 2000, className: fieldTextarea }}
                />
              </div>
            </div>

            <div className={inputHolder}>
              <div>
                <Typography variant="h2">Details</Typography>
              </div>
              <div className="space-y-4">
                <div>
                  <PillSelect
                    label="Category"
                    multi
                    limit={3}
                    values={props.collection.tags}
                    options={COLLECTION_SETTINGS.COLLECTION_TAGS.map((g) => ({
                      value: g,
                      name: g,
                    }))}
                    onChange={(tags) => merge({ tags })}
                  />
                </div>
                <div>
                  <PillSelect
                    invalid={validate('targetArea')}
                    label="Target Area"
                    multi
                    limit={2}
                    values={props.collection.targetArea}
                    options={COLLECTION_SETTINGS.COLLECTION_TARGET_AREA.map(
                      (g) => ({
                        value: g,
                        name: g,
                      }),
                    )}
                    onChange={targetAreaChanged}
                  />
                </div>
                <div className="flex flex-col">
                  <label className={clsx(fieldLabel, 'mb-2')}>Equipment</label>
                  <TagEditor
                    onChange={equipmentChanged}
                    tags={props.collection.requiredEquipment || []}
                    placeholder="Type keyword here..."
                    maxLength={20}
                    maxItems={7}
                    allowSpaces={true}
                  />
                  <div className="mt-2">
                    <small>
                      Press Enter after typing to add new one <br />
                      Example: bands, kettlebell, mat, basketball
                    </small>
                  </div>
                </div>

                <PillSelect
                  label="Difficulty"
                  Pill={SquarePill}
                  values={props.collection.difficulty ?? ''}
                  options={COLLECTION_SETTINGS.COLLECTION_DIFFICULTY}
                  onChange={difficultyChanged}
                  gap={0}
                />
                <PillSelect
                  label="Location"
                  Pill={SquarePill}
                  values={props.collection.location ?? ''}
                  options={COLLECTION_SETTINGS.COLLECTION_LOCATION}
                  onChange={locationChanged}
                  gap={0}
                />
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <Typography variant="h2">Settings</Typography>
            <div className="max-w-xl">
              <PublishingBehaviourSelect
                publishingBehaviour={props.collection.publishingBehaviour}
                invalid={validate('publishingBehaviour')}
                onChange={publishingBehaviourChanged}
              />
              <RemoveWorkoutLimitModal
                isOpen={isRemoveLimitOpen}
                onConfirm={confirmRemoveLimit}
                onCancel={() => setIsRemoveLimitOpen(false)}
              />
              <div className="flex flex-col">
                <label className={clsx(fieldLabel, 'mb-2')}>
                  Workout Limit (optional)
                </label>
                <TextField
                  id="workout-limit"
                  className="reps"
                  style={{ width: '100px' }}
                  type="number"
                  // pattern="\d*"
                  value={props.collection.workoutLimit ?? ''}
                  onChange={workoutLimitChanged}
                />
              </div>
            </div>
          </section>

          <section className="relative">
            <Typography variant="h2">
              FAQ <span className="text-lg font-normal">(optional)</span>
            </Typography>
            <div className="absolute right-6 top-6">
              <Toggle
                value={!props.collection.hiddenSections?.faq}
                onChange={() => toggleSection('faq')}
              />
            </div>
            <Faq
              questions={props.collection.faq}
              onChange={faqChanged}
              tree={
                props.validationTree && props.validationTree.faq
                  ? props.validationTree.faq
                  : null
              }
            />
          </section>

          <section className="relative">
            <div className={bigHeading}>
              <div
                style={{
                  borderBottom: validate('testimonials')
                    ? '1px solid red'
                    : undefined,
                }}
              >
                <Typography variant="h2">
                  Reviews{' '}
                  <span className="text-lg font-normal">(optional)</span>
                </Typography>
              </div>
            </div>
            <div className="absolute right-6 top-6">
              <Toggle
                value={!props.collection.hiddenSections?.testimonials}
                onChange={() => toggleSection('testimonials')}
              />
            </div>
            <div>
              <ProgramReviews
                onChange={(testimonials) => merge({ testimonials })}
                value={props.collection.testimonials || []}
                errors={[]}
              />
            </div>
          </section>
        </div>
      </main>
      <StatusBar.Component />
    </div>
  );
};
